import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  estimateApi,
  type Estimate,
  type EstimateItem,
  type EstimateSection,
  type CreateEstimateItemRequest,
  type UpdateEstimateItemRequest,
} from '../api/estimate-service';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { ApiClientError } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('useEstimateItems');

function reportError(error: unknown, fallback: string) {
  const message =
    error instanceof ApiClientError
      ? error.getFirstError()
      : error instanceof Error
        ? error.message
        : fallback;
  toast.error(message);
}

/**
 * Item create/update/delete mutations for one estimate.
 *
 * Server recomputes `materials_total` / `labor_total` / `grand_total` after
 * every item mutation and broadcasts `estimates/updated` over WS — so on
 * success we invalidate the detail query (rather than trusting an optimistic
 * `line_total`) to pull the authoritative totals. Optimistic writes here are
 * only for instant row-level feedback; totals always come from the refetch.
 */
export function useEstimateItems(conceptId: string, estimateId: string) {
  const queryClient = useQueryClient();
  const detailKey = cacheKeys.estimates.detail(estimateId);
  const t = useTranslations('common.estimate.editor');
  const tCommon = useTranslations('common');

  // Id of the just-added line item (add button or delete-undo restore): the
  // row scrolls itself into view and runs the flash animation, then clears
  // the marker via clearAddedItemHighlight (cross-platform estimate-editor
  // pattern).
  const [lastAddedItemId, setLastAddedItemId] = useState<string | null>(null);
  const clearAddedItemHighlight = useCallback(
    () => setLastAddedItemId(null),
    []
  );

  const invalidateDetail = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: detailKey });
    queryClient.invalidateQueries({
      queryKey: cacheKeys.estimates.list(conceptId),
    });
  }, [queryClient, detailKey, conceptId]);

  const createItemMutation = useMutation({
    mutationFn: (data: CreateEstimateItemRequest) =>
      estimateApi.createItem(estimateId, data),
    onSuccess: (item) => {
      // Mark the new row for scroll-into-view + flash; the row appears once
      // the detail refetch below lands (no optimistic insert on web).
      setLastAddedItemId(item.id);
      invalidateDetail();
    },
    onError: (error) => {
      logger.error('Create item failed', { estimateId, error });
      reportError(error, 'Failed to add item');
      invalidateDetail();
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: ({
      itemId,
      data,
    }: {
      itemId: string;
      data: UpdateEstimateItemRequest;
    }) => estimateApi.updateItem(estimateId, itemId, data),
    onMutate: async ({ itemId, data }) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<Estimate>(detailKey);
      if (previous) {
        queryClient.setQueryData<Estimate>(detailKey, {
          ...previous,
          items: previous.items.map((item) =>
            item.id === itemId ? applyOptimisticItem(item, data) : item
          ),
        });
      }
      return { previous };
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(detailKey, context.previous);
      }
      logger.error('Update item failed', { estimateId, error });
      reportError(error, 'Failed to update item');
    },
    // Totals are recomputed server-side — always reconcile from the API.
    onSettled: invalidateDetail,
  });

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => estimateApi.deleteItem(estimateId, itemId),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<Estimate>(detailKey);
      if (previous) {
        queryClient.setQueryData<Estimate>(detailKey, {
          ...previous,
          items: previous.items.filter((item) => item.id !== itemId),
        });
      }
      return { previous };
    },
    onSuccess: (_data, itemId, context) => {
      // Undo = re-create through the normal POST with the snapshot's fields
      // and its ORIGINAL sort_order (no restore endpoint; deletes leave
      // sort_order gaps, so the slot is still free). The currency and
      // measurement-system echoes use the display denomination captured at
      // delete time — if either changed before the undo, the server 422s (an
      // honest error; the values would be re-denominated wrongly).
      const snapshot = context?.previous?.items.find((i) => i.id === itemId);
      const currency = context?.previous?.currency;
      const measurementSystem = context?.previous?.measurement_system;
      if (!snapshot) return;
      toast.undoable(t('itemDeleted'), {
        action: {
          label: tCommon('undo'),
          onClick: () =>
            createItemMutation.mutate({
              section: snapshot.section,
              category: snapshot.category,
              name: snapshot.name,
              quantity: snapshot.quantity,
              unit: snapshot.unit,
              unit_price: snapshot.unit_price,
              sort_order: snapshot.sort_order,
              ...(currency ? { currency } : {}),
              ...(measurementSystem
                ? { measurement_system: measurementSystem }
                : {}),
            }),
        },
      });
    },
    onError: (error, _itemId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(detailKey, context.previous);
      }
      logger.error('Delete item failed', { estimateId, error });
      reportError(error, 'Failed to delete item');
    },
    onSettled: invalidateDetail,
  });

  const reorderItemsMutation = useMutation({
    mutationFn: ({
      section,
      itemIds,
    }: {
      section: EstimateSection;
      itemIds: string[];
    }) => estimateApi.reorderItems(estimateId, section, itemIds),
    onMutate: async ({ section, itemIds }) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<Estimate>(detailKey);
      if (previous) {
        // Optimistic re-stamp: sort_order = index per the dropped order, so
        // the UI keeps it with no flicker until the server confirms.
        const orderIndex = new Map(itemIds.map((id, index) => [id, index]));
        queryClient.setQueryData<Estimate>(detailKey, {
          ...previous,
          items: previous.items.map((item) => {
            const index = orderIndex.get(item.id);
            return item.section === section && index !== undefined
              ? { ...item, sort_order: index }
              : item;
          }),
        });
      }
      return { previous };
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(detailKey, context.previous);
      }
      logger.error('Reorder items failed', { estimateId, error });
      reportError(error, 'Failed to reorder items');
    },
    onSettled: invalidateDetail,
  });

  /**
   * Write an item edit into the cache IMMEDIATELY (called per keystroke),
   * recomputing `line_total` locally. The debounced PATCH (whose `onMutate`
   * re-applies the same write) follows later — without this, the row total and
   * the totals summary would only move after the debounce window instead of
   * tracking typing. The server reconciles on settle as usual.
   */
  const applyItemDraft = useCallback(
    (itemId: string, data: UpdateEstimateItemRequest) => {
      const previous = queryClient.getQueryData<Estimate>(detailKey);
      if (!previous) return;
      queryClient.setQueryData<Estimate>(detailKey, {
        ...previous,
        items: previous.items.map((item) =>
          item.id === itemId ? applyOptimisticItem(item, data) : item
        ),
      });
    },
    [queryClient, detailKey]
  );

  return {
    createItem: createItemMutation.mutate,
    isCreatingItem: createItemMutation.isPending,
    updateItem: updateItemMutation.mutate,
    applyItemDraft,
    deleteItem: deleteItemMutation.mutate,
    reorderItems: reorderItemsMutation.mutate,
    lastAddedItemId,
    clearAddedItemHighlight,
  };
}

/**
 * Apply a partial item patch to the cached row, recomputing `line_total` only
 * locally for instant feedback (qty × price). The server's recomputed total
 * overwrites this on settle.
 */
function applyOptimisticItem(
  item: EstimateItem,
  data: UpdateEstimateItemRequest
): EstimateItem {
  const next = { ...item, ...data };
  const quantity = data.quantity ?? item.quantity;
  const unitPrice = data.unit_price ?? item.unit_price;
  next.line_total = roundCents(quantity * unitPrice);
  return next;
}

function roundCents(value: number): number {
  return Math.round(value * 100) / 100;
}
